import { useState, useMemo } from "react";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { FIELD_CLASSES } from "@shared/utils/constants";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  useChoices,
  useGetAllSOPs,
  useMeterDetail,
  usePostWorkOrderJobCard,
  usePriority,
} from "@features/mx/device-management/hooks";
import { useParams } from "react-router-dom";
import { mapToLabelValue } from "@shared/utils/mapToLabelValue";

interface CreateServiceOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CreateServiceOrderModal({
  isOpen,
  onClose,
}: CreateServiceOrderModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const remoteUtilityId = useRemoteUtilityId();
  const { id } = useParams();
  const meterId = Number(id);
  const [serviceType, setServiceType] = useState("Meter Replacement");
  const { data: deviceDetails } = useMeterDetail(meterId, remoteUtilityId);
  const { mutate: createJobCard } = usePostWorkOrderJobCard();
  const { data: priorityData } = usePriority();
  const { data: serviceTypesData } = useChoices({
    choice: "service_order_operation_type",
  });

  const serviceTypeOptions =
    serviceTypesData?.map((serviceTypes) => ({
      value: serviceTypes.key,
      label: serviceTypes.value,
    })) || [];
  const priorityOptions =
    priorityData?.result?.map((priority) => ({
      value: priority.key,
      label: priority.value,
    })) || [];

  const { data: sops } = useGetAllSOPs({
    remoteUtilityId,
    operationType: serviceType,
  });

  const sopList = mapToLabelValue(sops?.result, "name", "id");
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "serviceType",
        label: "Service Type",
        type: "select",
        required: true,
        placeholder: "Select work type",
        options: serviceTypeOptions,
      },
      {
        name: "sop",
        type: "select",
        label: "SOP Template",
        options: sopList,
        required: true,
        placeholder: "Select service oder",
      },
      {
        name: "priority",
        label: "Priority",
        type: "select",
        required: true,
        placeholder: "Select priority",
        options: priorityOptions,
      },
      {
        name: "date",
        label: "Expected Date",
        type: "datepicker",
        required: true,
        allowPast: false,
        placeholder: "Select date",
      },

      {
        name: "description",
        label: "Description",
        type: "textarea",
        placeholder: "Enter description",
        className: "col-span-2",
      },
    ],
    [serviceTypeOptions, priorityOptions, sopList],
  );

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    defaultValues: {
      workType: "",
      priority: "",
      date: "",
      description: "",
    },
  });

  const handleSubmit = (data: any) => {
    setIsSubmitting(true);

    const payload = {
      additional_data: {
        assignment_status: deviceDetails?.status,
        device_no: deviceDetails?.deviceNo,
        physical_status: deviceDetails?.physicalStatus,
        reading_status: deviceDetails?.status,
      },
      latitude: deviceDetails?.lat,
      area: deviceDetails?.areaCode,
      sub_area: deviceDetails?.subAreaCode,
      longitude: deviceDetails?.long,
      premise: deviceDetails?.premise,
      remark: data.description,
      remote_utility_id: remoteUtilityId,
      request_account_no: deviceDetails?.meterNumber,
      request_id: id,
      priority: data.priority,
      sop: data.sop,
      schedule_date: data.date,
    };

    // ✅ Actually call the hook mutation
    createJobCard(payload, {
      onSuccess: () => {
        setIsSubmitting(false);
        onClose();
      },
      onError: (err: any) => {
        setIsSubmitting(false);
      },
    });
  };

  const handleFieldChange = (fieldName, value) => {
    if (fieldName === "serviceType") {
      setServiceType(value);
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create Service Order</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-200px)] px-1">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onFieldChange={handleFieldChange}
            gridColumns={2}
            config={{
              showSubmitButton: false,
              showResetButton: false,
              layout: "vertical",
              spacing: "comfortable",
              classes: FIELD_CLASSES,
            }}
            renderCustomFooter={() => (
              <div className="flex items-center justify-end gap-3 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Creating..." : "Create"}
                </Button>
              </div>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
